import { Actor, Canvas, Engine, Random, vec } from 'excalibur';
import { CLOCK_CENTER_X, CLOCK_CENTER_Y, FACE_CANVAS_SIZE, RIM_RADIUS, SEEDS } from '../utils/constants';
import { Palette } from '../utils/palette';
import {
  Expression,
  FUNNY_EXPRESSIONS,
  css,
  drawFace,
  paintClockBase,
  wobblyCirclePath,
} from '../utils/sketch';

export class ClockFace extends Actor {
  private readonly faceCanvas: Canvas;
  private readonly rng: Random;
  private expression: Expression = 'idle';
  private baseExpression: Expression = 'idle';
  private expressionTimer = 0;
  private funnyTimer = 4;
  private blinkTimer = 2.6;
  private blinking = false;

  constructor() {
    const canvas = new Canvas({
      width: FACE_CANVAS_SIZE,
      height: FACE_CANVAS_SIZE,
      cache: true,
      draw: (ctx) => this.paint(ctx),
    });
    super({ name: 'clock-face', pos: vec(CLOCK_CENTER_X, CLOCK_CENTER_Y), graphic: canvas });
    this.faceCanvas = canvas;
    this.rng = new Random(SEEDS.face);
  }

  setExpression(expression: Expression): void {
    this.expression = expression;
    this.expressionTimer = 0.9;
    this.faceCanvas.flagDirty();
  }

  setBaseExpression(expression: Expression): void {
    if (this.baseExpression === expression) {
      return;
    }
    this.baseExpression = expression;
    if (this.expressionTimer <= 0) {
      this.expression = expression;
      this.faceCanvas.flagDirty();
    }
  }

  refreshTheme(): void {
    this.faceCanvas.flagDirty();
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    if (this.expressionTimer > 0) {
      this.expressionTimer -= dt;
      if (this.expressionTimer <= 0) {
        this.expressionTimer = 0;
        this.expression = this.baseExpression;
        this.faceCanvas.flagDirty();
      }
    }
    this.funnyTimer -= dt;
    if (this.funnyTimer <= 0) {
      this.funnyTimer = this.rng.floating(4.5, 9);
      if (this.expressionTimer <= 0) {
        this.expression = this.rng.pickOne(FUNNY_EXPRESSIONS);
        this.expressionTimer = 1.1;
        this.faceCanvas.flagDirty();
      }
    }
    this.blinkTimer -= dt;
    if (this.blinkTimer <= 0) {
      this.blinking = !this.blinking;
      this.blinkTimer = this.blinking ? 0.13 : this.rng.floating(2.2, 5.2);
      this.faceCanvas.flagDirty();
    }
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const mid = FACE_CANVAS_SIZE / 2;
    const rng = new Random(SEEDS.face);

    ctx.save();
    ctx.fillStyle = css(Palette.shadow, 0.045);
    for (let i = 0; i < 5; i++) {
      wobblyCirclePath(ctx, mid + 7, mid + 13, RIM_RADIUS + 9 + i * 6, rng, 3, 56);
      ctx.fill();
    }
    ctx.restore();

    paintClockBase(ctx, mid, mid, rng, {
      radius: RIM_RADIUS,
      faceLight: Palette.cream,
      faceShade: Palette.paperDeep,
      rim: Palette.ink,
      tick: Palette.ink,
      accents: [Palette.mint, Palette.yellow, Palette.pink, Palette.blue],
    });

    speckleFace(ctx, mid, mid, RIM_RADIUS - 26, rng);

    drawFace(ctx, mid, mid + RIM_RADIUS * 0.02, RIM_RADIUS * 0.55, rng, this.expression, this.blinking);
  }
}

function speckleFace(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rng: Random,
): void {
  ctx.save();
  for (let i = 0; i < 90; i++) {
    const angle = rng.floating(0, Math.PI * 2);
    const distance = Math.sqrt(rng.floating(0, 1)) * radius;
    const x = cx + Math.cos(angle) * distance;
    const y = cy + Math.sin(angle) * distance;
    ctx.fillStyle = css(Palette.ink, rng.floating(0.015, 0.045));
    ctx.beginPath();
    ctx.arc(x, y, rng.floating(0.5, 1.6), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
